import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  LinearProgress,
  MenuItem,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';

import ClientDropdown from '@src/components/ClientDropdown';
import { useMessage } from '@src/context/common';
import paths from '@src/paths';
import {
  createFacebookPage,
  listFacebookPages,
  updateFacebookPage,
} from '@src/api/facebookPages';

// Meta sets these from a Page's highest spending month since September 2019 and
// only ever raises them. We can't read the tier through the API, so it's entered here.
const TIERS = [250, 1000, 5000, 20000];

const num = (n) => (n || 0).toLocaleString();

export default function FacebookPagesPage() {
  const message = useMessage();
  const [searchParams, setSearchParams] = useSearchParams();

  const clientParam = searchParams.get('client');
  const [clientId, setClientId] = useState(clientParam || '');
  const [showInactive, setShowInactive] = useState(false);
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await listFacebookPages({
        client: clientId || undefined,
        includeInactive: showInactive,
      });
      setPages(Array.isArray(data) ? data : data?.results || []);
    } catch (error) {
      message.error(error?.response?.data?.detail || 'Could not load Pages.');
    } finally {
      setLoading(false);
    }
  }, [clientId, showInactive, message]);

  useEffect(() => {
    load();
  }, [load]);

  const selectClient = (value) => {
    setClientId(value || '');
    const next = new URLSearchParams(searchParams);
    if (value) next.set('client', value);
    else next.delete('client');
    setSearchParams(next, { replace: true });
  };

  const save = async (page, changes, successText) => {
    try {
      await updateFacebookPage(page.id, changes);
      if (successText) message.success(successText);
      load();
    } catch (error) {
      message.error(error?.response?.data?.detail || 'Could not save that change.');
      load();
    }
  };

  return (
    <Stack spacing={3} sx={{ p: 3, maxWidth: 1200 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ sm: 'flex-end' }}
        spacing={2}
      >
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Facebook Pages
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 680 }}>
            Every ad publishes under a Page, and each Page can only hold so many
            ads. Give Pages nicknames so identically named ones can be told apart,
            and keep each Page&apos;s ad limit up to date.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            component={RouterLink}
            to={clientId ? `${paths.facebook.localAdBuilder}?client=${clientId}` : paths.facebook.localAdBuilder}
          >
            Local Ad Builder
          </Button>
          <Button variant="contained" onClick={() => setAdding(true)}>
            Add Page
          </Button>
        </Stack>
      </Stack>

      <Card variant="outlined">
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
            <Box sx={{ flex: 1, maxWidth: 420 }}>
              <ClientDropdown
                value={clientId}
                onChange={(value) => selectClient(value)}
                label="Client (leave blank for all)"
              />
            </Box>
            <FormControlLabel
              control={<Switch checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />}
              label="Show deactivated"
            />
          </Stack>
        </CardContent>
      </Card>

      <Card variant="outlined">
        {loading && <LinearProgress />}
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {!clientId && <TableCell>Client</TableCell>}
                <TableCell>Nickname</TableCell>
                <TableCell>Facebook name · Page ID</TableCell>
                <TableCell>
                  <Tooltip title="Meta sets this from the Page's highest spending month since Sept 2019. It only goes up.">
                    <span>Ad limit</span>
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ minWidth: 160 }}>Ads on Page</TableCell>
                <TableCell>Local ads</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading && pages.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      {clientId
                        ? 'No Pages for this client yet. Add one to let their ads publish.'
                        : 'No Pages yet.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {pages.map((page) => (
                <PageRow
                  key={page.id}
                  page={page}
                  showClient={!clientId}
                  onSave={save}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      <AddPageDialog
        open={adding}
        defaultClientId={clientId}
        onClose={() => setAdding(false)}
        onAdded={(created) => {
          setAdding(false);
          message.success(`Added ${created.display_name}.`);
          load();
        }}
      />
    </Stack>
  );
}

function PageRow({ page, showClient, onSave }) {
  const [nickname, setNickname] = useState(page.nickname || '');

  useEffect(() => {
    setNickname(page.nickname || '');
  }, [page.nickname]);

  const commitNickname = () => {
    if (nickname.trim() !== (page.nickname || '')) {
      onSave(page, { nickname: nickname.trim() });
    }
  };

  return (
    <TableRow hover sx={{ opacity: page.active ? 1 : 0.55 }}>
      {showClient && (
        <TableCell>
          <Typography variant="body2">{page.client_name}</Typography>
        </TableCell>
      )}
      <TableCell sx={{ minWidth: 200 }}>
        <TextField
          size="small"
          placeholder="Add a nickname"
          value={nickname}
          onChange={(e) => setNickname(e.target.value)}
          onBlur={commitNickname}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.currentTarget.blur();
          }}
          inputProps={{ maxLength: 100, 'aria-label': 'Nickname' }}
          fullWidth
        />
      </TableCell>
      <TableCell>
        <Typography variant="body2">{page.page_name}</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
          {page.page_id}
          {' · '}
          <a href={page.ads_library_url} target="_blank" rel="noreferrer">
            Ads Library
          </a>
        </Typography>
      </TableCell>
      <TableCell>
        <TextField
          select
          size="small"
          value={page.ad_tier}
          onChange={(e) => onSave(page, { ad_tier: Number(e.target.value) }, 'Ad limit updated.')}
          inputProps={{ 'aria-label': 'Ad limit' }}
          sx={{ minWidth: 100 }}
        >
          {TIERS.map((t) => (
            <MenuItem key={t} value={t}>
              {num(t)}
            </MenuItem>
          ))}
        </TextField>
      </TableCell>
      <TableCell>
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="caption" color="text.secondary">
            {num(page.ads_used)} / {num(page.ad_tier)}
          </Typography>
          {page.capacity_status !== 'ok' && (
            <Typography
              variant="caption"
              color={page.capacity_status === 'full' ? 'error.main' : 'warning.main'}
              fontWeight={600}
            >
              {page.capacity_status === 'full' ? 'Full' : `${Math.round(page.usage_pct)}%`}
            </Typography>
          )}
        </Stack>
        <LinearProgress
          variant="determinate"
          value={Math.min(page.usage_pct, 100)}
          color={page.capacity_status === 'full' ? 'error' : page.capacity_status === 'warning' ? 'warning' : 'primary'}
          sx={{ height: 6, borderRadius: 3, mt: 0.5 }}
        />
      </TableCell>
      <TableCell>
        {page.reserved_for_local ? (
          <Tooltip title="Change this in the Local Ad Builder.">
            <Chip size="small" color="primary" label="Used for local" />
          </Tooltip>
        ) : (
          <Typography variant="caption" color="text.secondary">
            —
          </Typography>
        )}
      </TableCell>
      <TableCell align="right">
        {page.active ? (
          <Tooltip
            title={
              page.reserved_for_local
                ? "This is the Page local ads use. Deactivating it clears that choice, and you'll need to pick another before publishing."
                : 'Hides this Page everywhere. Ads already on it are unaffected.'
            }
          >
            <Button size="small" color="inherit" onClick={() => onSave(page, { active: false }, 'Page deactivated.')}>
              Deactivate
            </Button>
          </Tooltip>
        ) : (
          <Button size="small" onClick={() => onSave(page, { active: true }, 'Page restored.')}>
            Restore
          </Button>
        )}
      </TableCell>
    </TableRow>
  );
}

const EMPTY = { page_id: '', page_name: '', nickname: '', ad_tier: 1000 };

function AddPageDialog({ open, defaultClientId, onClose, onAdded }) {
  const [clientId, setClientId] = useState(defaultClientId || '');
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setClientId(defaultClientId || '');
      setForm(EMPTY);
      setErrors({});
    }
  }, [open, defaultClientId]);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const submit = async () => {
    setSaving(true);
    setErrors({});
    try {
      const { data } = await createFacebookPage({ ...form, client: clientId });
      onAdded(data);
    } catch (error) {
      const body = error?.response?.data || {};
      setErrors(
        body.detail ? { detail: body.detail } : Object.fromEntries(
          Object.entries(body).map(([k, v]) => [k, Array.isArray(v) ? v.join(' ') : String(v)]),
        ),
      );
    } finally {
      setSaving(false);
    }
  };

  const ready = clientId && form.page_id.trim() && form.page_name.trim();

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Add a Facebook Page</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {errors.detail && <Alert severity="error">{errors.detail}</Alert>}
          <ClientDropdown value={clientId} onChange={(value) => setClientId(value || '')} label="Client" />
          <TextField
            label="Page ID"
            value={form.page_id}
            onChange={set('page_id')}
            error={Boolean(errors.page_id)}
            helperText={
              errors.page_id
              || 'The long number from the Page’s About section, or from view_all_page_id= in its Ads Library link.'
            }
            inputProps={{ inputMode: 'numeric' }}
          />
          <TextField
            label="Facebook name"
            value={form.page_name}
            onChange={set('page_name')}
            error={Boolean(errors.page_name)}
            helperText={errors.page_name || 'As it appears on Facebook.'}
          />
          <TextField
            label="Nickname (optional)"
            value={form.nickname}
            onChange={set('nickname')}
            helperText="Something your team will recognise, e.g. “Local — Texas”."
            inputProps={{ maxLength: 100 }}
          />
          <TextField
            select
            label="Ad limit"
            value={form.ad_tier}
            onChange={set('ad_tier')}
            helperText="Set by Meta from the Page's highest spending month. Leave at 1,000 if unsure."
          >
            {TIERS.map((t) => (
              <MenuItem key={t} value={t}>
                {num(t)} ads
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!ready || saving} onClick={submit}>
          Add Page
        </Button>
      </DialogActions>
    </Dialog>
  );
}
